/**
 * Color manipulation and validation utilities
 * Handles color conversions, contrast calculations, and accessibility checks
 */
export class ColorUtils {
  /**
   * Convert any color format to RGB object
   * @param {string} color - Color in any format (hex, rgb, rgba)
   * @returns {{r: number, g: number, b: number, a?: number}} RGB object
   */
  static toRGB(color) {
    if (!color) return null;
    
    // Remove spaces and convert to lowercase
    color = color.replace(/\s/g, '').toLowerCase();
    
    // Handle hex colors
    if (color.startsWith('#')) {
      return this.hexToRGB(color);
    }
    
    // Handle rgb/rgba
    if (color.startsWith('rgb')) {
      return this.parseRGB(color);
    }
    
    // Handle named colors (basic set)
    const namedColors = {
      white: { r: 255, g: 255, b: 255 },
      black: { r: 0, g: 0, b: 0 },
      red: { r: 255, g: 0, b: 0 },
      green: { r: 0, g: 128, b: 0 },
      blue: { r: 0, g: 0, b: 255 }
    };
    
    return namedColors[color] || null;
  }
  
  /**
   * Convert hex color to RGB
   * @param {string} hex - Hex color code
   * @returns {{r: number, g: number, b: number}} RGB object
   */
  static hexToRGB(hex) {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Handle 3-digit hex
    if (hex.length === 3) {
      hex = hex.split('').map(char => char + char).join('');
    }
    
    // Convert to RGB
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    return { r, g, b };
  }
  
  /**
   * Parse rgb/rgba string to object
   * @param {string} rgbString - RGB string
   * @returns {{r: number, g: number, b: number, a?: number}} RGB object
   */
  static parseRGB(rgbString) {
    const match = rgbString.match(/rgba?\((\d+),(\d+),(\d+),?(\d*\.?\d*)\)/);
    if (!match) return null;
    
    return {
      r: parseInt(match[1]),
      g: parseInt(match[2]),
      b: parseInt(match[3]),
      a: match[4] ? parseFloat(match[4]) : 1
    };
  }
  
  /**
   * Convert RGB to hex color
   * @param {{r: number, g: number, b: number}} rgb - RGB object
   * @returns {string} Hex color code
   */
  static rgbToHex(rgb) {
    const toHex = (n) => {
      const hex = Math.round(n).toString(16).padStart(2, '0');
      return hex;
    };
    
    return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
  }
  
  /**
   * Convert RGB to CSS rgb() string
   * @param {{r: number, g: number, b: number, a?: number}} rgb - RGB object
   * @returns {string} CSS rgb() or rgba() string
   */
  static rgbToString(rgb) {
    if (rgb.a !== undefined && rgb.a !== 1) {
      return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${rgb.a})`;
    }
    return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
  }
  
  /**
   * Calculate relative luminance (WCAG formula)
   * @param {{r: number, g: number, b: number}} rgb - RGB object
   * @returns {number} Relative luminance (0-1)
   */
  static getLuminance(rgb) {
    const rsRGB = rgb.r / 255;
    const gsRGB = rgb.g / 255;
    const bsRGB = rgb.b / 255;
    
    const r = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
    const g = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
    const b = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);
    
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }
  
  /**
   * Calculate contrast ratio between two colors (WCAG formula)
   * @param {string} color1 - First color
   * @param {string} color2 - Second color
   * @returns {number} Contrast ratio (1-21)
   */
  static getContrastRatio(color1, color2) {
    const rgb1 = this.toRGB(color1);
    const rgb2 = this.toRGB(color2);
    
    if (!rgb1 || !rgb2) return 1;
    
    const lum1 = this.getLuminance(rgb1);
    const lum2 = this.getLuminance(rgb2);
    
    const lighter = Math.max(lum1, lum2);
    const darker = Math.min(lum1, lum2);
    
    return (lighter + 0.05) / (darker + 0.05);
  }
  
  /**
   * Check if color meets WCAG contrast requirements
   * @param {string} foreground - Foreground color
   * @param {string} background - Background color
   * @param {string} level - WCAG level ('AA' or 'AAA')
   * @param {boolean} largeText - Whether text is large (18pt+ or 14pt+ bold)
   * @returns {boolean} Whether contrast meets requirements
   */
  static meetsWCAG(foreground, background, level = 'AA', largeText = false) {
    const ratio = this.getContrastRatio(foreground, background);
    
    const requirements = {
      AA: { normal: 4.5, large: 3 },
      AAA: { normal: 7, large: 4.5 }
    };
    
    const required = requirements[level][largeText ? 'large' : 'normal'];
    return ratio >= required;
  }
  
  /**
   * Adjust color to meet target contrast ratio
   * @param {string} foreground - Foreground color to adjust
   * @param {string} background - Background color
   * @param {number} targetRatio - Target contrast ratio
   * @returns {string} Adjusted color
   */
  static adjustForContrast(foreground, background, targetRatio = 4.5) {
    let rgb = this.toRGB(foreground);
    const bgRgb = this.toRGB(background);
    
    if (!rgb || !bgRgb) return foreground;
    
    let currentRatio = this.getContrastRatio(
      this.rgbToString(rgb),
      this.rgbToString(bgRgb)
    );
    
    // If already meets target, return original
    if (currentRatio >= targetRatio) return foreground;
    
    // Determine if we should lighten or darken
    const bgLuminance = this.getLuminance(bgRgb);
    const shouldLighten = bgLuminance < 0.5;
    
    // Adjust in steps until we meet target
    let step = 0;
    const maxSteps = 100;
    
    while (currentRatio < targetRatio && step < maxSteps) {
      if (shouldLighten) {
        rgb = this.lightenRGB(rgb, 2);
      } else {
        rgb = this.darkenRGB(rgb, 2);
      }
      
      currentRatio = this.getContrastRatio(
        this.rgbToString(rgb),
        this.rgbToString(bgRgb)
      );
      step++;
    }
    
    return this.rgbToHex(rgb);
  }
  
  /**
   * Lighten color by percentage
   * @param {string} color - Color to lighten
   * @param {number} percent - Percentage to lighten (0-100)
   * @returns {string} Lightened color
   */
  static lighten(color, percent) {
    const rgb = this.toRGB(color);
    if (!rgb) return color;
    
    const lightened = this.lightenRGB(rgb, percent);
    return this.rgbToHex(lightened);
  }
  
  /**
   * Lighten RGB object
   * @private
   */
  static lightenRGB(rgb, percent) {
    const factor = percent / 100;
    return {
      r: Math.min(255, rgb.r + (255 - rgb.r) * factor),
      g: Math.min(255, rgb.g + (255 - rgb.g) * factor),
      b: Math.min(255, rgb.b + (255 - rgb.b) * factor)
    };
  }
  
  /**
   * Darken color by percentage
   * @param {string} color - Color to darken
   * @param {number} percent - Percentage to darken (0-100)
   * @returns {string} Darkened color
   */
  static darken(color, percent) {
    const rgb = this.toRGB(color);
    if (!rgb) return color;
    
    const darkened = this.darkenRGB(rgb, percent);
    return this.rgbToHex(darkened);
  }
  
  /**
   * Darken RGB object
   * @private
   */
  static darkenRGB(rgb, percent) {
    const factor = 1 - (percent / 100);
    return {
      r: Math.max(0, rgb.r * factor),
      g: Math.max(0, rgb.g * factor),
      b: Math.max(0, rgb.b * factor)
    };
  }
  
  /**
   * Generate color with alpha channel
   * @param {string} color - Base color
   * @param {number} alpha - Alpha value (0-1)
   * @returns {string} RGBA color string
   */
  static toAlpha(color, alpha) {
    const rgb = this.toRGB(color);
    if (!rgb) return color;
    
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
  }
  
  /**
   * Generate tint (mix with white)
   * @param {string} color - Base color
   * @param {number} percent - Percentage of white to mix (0-100)
   * @returns {string} Tinted color
   */
  static generateTint(color, percent) {
    return this.mix(color, '#FFFFFF', percent);
  }
  
  /**
   * Generate shade (mix with black)
   * @param {string} color - Base color
   * @param {number} percent - Percentage of black to mix (0-100)
   * @returns {string} Shaded color
   */
  static generateShade(color, percent) {
    return this.mix(color, '#000000', percent);
  }
  
  /**
   * Mix two colors
   * @param {string} color1 - First color
   * @param {string} color2 - Second color
   * @param {number} percent - Percentage of second color (0-100)
   * @returns {string} Mixed color
   */
  static mix(color1, color2, percent) {
    const rgb1 = this.toRGB(color1);
    const rgb2 = this.toRGB(color2);
    
    if (!rgb1 || !rgb2) return color1;
    
    const factor = percent / 100;
    
    const mixed = {
      r: Math.round(rgb1.r * (1 - factor) + rgb2.r * factor),
      g: Math.round(rgb1.g * (1 - factor) + rgb2.g * factor),
      b: Math.round(rgb1.b * (1 - factor) + rgb2.b * factor)
    };
    
    return this.rgbToHex(mixed);
  }
  
  /**
   * Check if color is light
   * @param {string} color - Color to check
   * @returns {boolean} True if light
   */
  static isLight(color) {
    const rgb = this.toRGB(color);
    if (!rgb) return true;
    
    const luminance = this.getLuminance(rgb);
    return luminance > 0.5;
  }
  
  /**
   * Check if color is dark
   * @param {string} color - Color to check
   * @returns {boolean} True if dark
   */
  static isDark(color) {
    return !this.isLight(color);
  }
  
  /**
   * Get best text color for background
   * @param {string} background - Background color
   * @param {string} lightColor - Light text option
   * @param {string} darkColor - Dark text option
   * @returns {string} Best text color
   */
  static getContrastingText(background, lightColor = '#FFFFFF', darkColor = '#1F2937') {
    const lightRatio = this.getContrastRatio(lightColor, background);
    const darkRatio = this.getContrastRatio(darkColor, background);
    
    // Prefer the color with better contrast
    return lightRatio > darkRatio ? lightColor : darkColor;
  }
  
  /**
   * Validate color format
   * @param {string} color - Color to validate
   * @returns {boolean} True if valid
   */
  static isValidColor(color) {
    if (!color) return false;
    
    // Try to convert to RGB
    const rgb = this.toRGB(color);
    if (!rgb) return false;
    
    // Check RGB values are in valid range
    return rgb.r >= 0 && rgb.r <= 255 &&
           rgb.g >= 0 && rgb.g <= 255 &&
           rgb.b >= 0 && rgb.b <= 255;
  }
  
  /**
   * Generate color scale (50-900)
   * @param {string} baseColor - Base color
   * @returns {Object} Color scale object
   */
  static generateColorScale(baseColor) {
    if (!this.isValidColor(baseColor)) return null;
    
    return {
      50: this.generateTint(baseColor, 95),
      100: this.generateTint(baseColor, 90),
      200: this.generateTint(baseColor, 80),
      300: this.generateTint(baseColor, 60),
      400: this.generateTint(baseColor, 40),
      500: baseColor,
      600: this.generateShade(baseColor, 20),
      700: this.generateShade(baseColor, 40),
      800: this.generateShade(baseColor, 60),
      900: this.generateShade(baseColor, 80)
    };
  }
  
  /**
   * Generate neutral scale based on primary color
   * @param {string} primaryColor - Primary color
   * @returns {Object} Neutral scale object
   */
  static generateNeutralScale(primaryColor) {
    // Mix primary with gray for brand-tinted neutrals
    const baseGray = '#6B7280';
    
    return {
      50: this.mix(this.generateTint(baseGray, 95), primaryColor, 2),
      100: this.mix(this.generateTint(baseGray, 90), primaryColor, 3),
      200: this.mix(this.generateTint(baseGray, 80), primaryColor, 4),
      300: this.mix(this.generateTint(baseGray, 60), primaryColor, 5),
      400: this.mix(this.generateTint(baseGray, 40), primaryColor, 6),
      500: this.mix(baseGray, primaryColor, 7),
      600: this.mix(this.generateShade(baseGray, 20), primaryColor, 8),
      700: this.mix(this.generateShade(baseGray, 40), primaryColor, 9),
      800: this.mix(this.generateShade(baseGray, 60), primaryColor, 10),
      900: this.mix(this.generateShade(baseGray, 80), primaryColor, 11)
    };
  }
}